// Developed by Softvative.com
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { useTheme } from '../../context/ThemeContext';
import { useIncidents } from '../../context/IncidentContext';
import { useAuth } from '../../context/AuthContext';

const { FiMenu, FiSearch, FiBell, FiUser, FiMoon, FiSun, FiSettings, FiLogOut, FiShield } = FiIcons;

const Header = ({ onToggleSidebar, sidebarCollapsed }) => {
    const { isDark, toggleTheme } = useTheme();
    const { signOut, user, tenant, isSuperAdmin, isTenantOwner } = useAuth();
    const navigate = useNavigate();
    const { incidents } = useIncidents();
    const [searchQuery, setSearchQuery] = useState('');
    const [showNotifications, setShowNotifications] = useState(false);
    const [showProfile, setShowProfile] = useState(false);
    const [searchResults, setSearchResults] = useState([]);

    const notifications = [
        {
            id: 1,
            type: 'critical',
            title: 'Critical incident INC-1024',
            description: 'SLA breaching in 1 hour',
            incidentId: 'INC-1024',
            time: '5 min ago'
        },
        {
            id: 2,
            type: 'info',
            title: 'New incident assigned',
            description: 'INC-1025 - Email server issues',
            incidentId: 'INC-1025',
            time: '15 min ago'
        },
        {
            id: 3,
            type: 'success',
            title: 'Incident resolved',
            description: 'INC-1023 - Network connectivity restored',
            incidentId: 'INC-1023',
            time: '1 hour ago'
        }
    ];

    const handleSearch = (query) => {
        setSearchQuery(query);
        if (query.length > 2) {
            const results = incidents.filter(incident =>
                incident.id.toLowerCase().includes(query.toLowerCase()) ||
                incident.summary.toLowerCase().includes(query.toLowerCase()) ||
                incident.description.toLowerCase().includes(query.toLowerCase()) ||
                incident.category.toLowerCase().includes(query.toLowerCase()) ||
                incident.service.toLowerCase().includes(query.toLowerCase())
            );
            setSearchResults(results.slice(0, 5));
        } else {
            setSearchResults([]);
        }
    };

    const handleNotificationClick = (notification) => {
        setShowNotifications(false);
        if (notification.incidentId) {
            navigate(`/incidents/${notification.incidentId}`);
        }
    };

    const handleProfileClick = () => {
        setShowProfile(false);
        navigate('/profile');
    };

    const handleSettingsClick = () => {
        setShowProfile(false);
        navigate('/settings');
    };

    const handleSignOut = async () => {
        setShowProfile(false);
        await signOut();
        navigate('/login');
    };

    const handleSearchResultClick = (incidentId) => {
        navigate(`/incidents/${incidentId}`);
        setSearchQuery('');
        setSearchResults([]);
    };

    // Get role display text
    const getRoleDisplay = () => {
        if (isSuperAdmin()) return 'Super Admin';
        if (isTenantOwner()) return 'Tenant Owner';
        return 'Tenant User';
    };

    // Get role badge color
    const getRoleBadgeColor = () => {
        if (isSuperAdmin()) return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200';
        if (isTenantOwner()) return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    };

    return (
        <header className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4 transition-colors">
            <div className="flex items-center justify-between">
                {/* Left side */}
                <div className="flex items-center space-x-4">
                    <button
                        onClick={onToggleSidebar}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                        aria-label="Toggle sidebar"
                    >
                        <SafeIcon icon={FiMenu} className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    </button>

                    {/* Tenant/System Context Badge */}
                    {isSuperAdmin() ? (
                        <div className="flex items-center space-x-2 px-3 py-1.5 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                            <SafeIcon icon={FiShield} className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                            <span className="text-sm font-medium text-purple-900 dark:text-purple-300">
                                System Admin
                            </span>
                        </div>
                    ) : tenant && (
                        <div className="flex items-center space-x-2 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/20 rounded-lg border border-indigo-200 dark:border-indigo-800">
                            <span className="text-sm font-medium text-indigo-900 dark:text-indigo-300">
                                {tenant.name}
                            </span>
                        </div>
                    )}

                    {/* Search */}
                    <div className="relative">
                        <SafeIcon
                            icon={FiSearch}
                            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400 dark:text-gray-500"
                        />
                        <input
                            type="text"
                            placeholder="Search incidents by ID, summary, category..."
                            value={searchQuery}
                            onChange={(e) => handleSearch(e.target.value)}
                            onFocus={() => searchQuery.length > 2 && setSearchResults([...searchResults])}
                            onBlur={() => setTimeout(() => setSearchResults([]), 200)}
                            className="pl-10 pr-4 py-2 w-96 bg-gray-100 dark:bg-gray-700 border-0 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 transition-colors"
                        />

                        {/* Search Results Dropdown */}
                        {searchResults.length > 0 && (
                            <motion.div
                                initial={{ opacity: 0, y: 10 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 max-h-64 overflow-y-auto"
                            >
                                {searchResults.map(result => (
                                    <div
                                        key={result.id}
                                        onClick={() => handleSearchResultClick(result.id)}
                                        className="p-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="font-medium text-sm text-gray-900 dark:text-white">{result.id}</p>
                                                <p className="text-sm text-gray-600 dark:text-gray-400 truncate">{result.summary}</p>
                                            </div>
                                            <span className={`px-2 py-1 text-xs rounded-full ${result.priority === 'Critical' ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200' :
                                                result.priority === 'High' ? 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200' :
                                                    result.priority === 'Medium' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200' :
                                                        'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                                                }`}>
                                                {result.priority}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </motion.div>
                        )}
                    </div>
                </div>

                {/* Right side */}
                <div className="flex items-center space-x-3">
                    {/* Theme toggle */}
                    <button
                        onClick={toggleTheme}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-all duration-200"
                        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                        title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                    >
                        <AnimatePresence mode="wait">
                            {isDark ? (
                                <motion.div
                                    key="sun"
                                    initial={{ rotate: -90, opacity: 0 }}
                                    animate={{ rotate: 0, opacity: 1 }}
                                    exit={{ rotate: 90, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <SafeIcon icon={FiSun} className="w-5 h-5 text-yellow-500" />
                                </motion.div>
                            ) : (
                                <motion.div
                                    key="moon"
                                    initial={{ rotate: 90, opacity: 0 }}
                                    animate={{ rotate: 0, opacity: 1 }}
                                    exit={{ rotate: -90, opacity: 0 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <SafeIcon icon={FiMoon} className="w-5 h-5 text-gray-600" />
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </button>

                    {/* Notifications */}
                    <div className="relative">
                        <button
                            onClick={() => setShowNotifications(!showNotifications)}
                            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors relative"
                            aria-label="Notifications"
                        >
                            <SafeIcon icon={FiBell} className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                            <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></span>
                        </button>

                        <AnimatePresence>
                            {showNotifications && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 10 }}
                                    className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
                                >
                                    <div className="p-4 border-b border-gray-200 dark:border-gray-700">
                                        <h3 className="font-semibold text-gray-900 dark:text-white">Notifications</h3>
                                    </div>
                                    <div className="max-h-96 overflow-y-auto">
                                        {notifications.map(notification => (
                                            <div
                                                key={notification.id}
                                                onClick={() => handleNotificationClick(notification)}
                                                className="p-4 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-b border-gray-100 dark:border-gray-700 last:border-b-0 transition-colors"
                                            >
                                                <div className="flex items-start space-x-3">
                                                    <div className={`w-2 h-2 rounded-full mt-2 ${notification.type === 'critical' ? 'bg-red-500' :
                                                        notification.type === 'success' ? 'bg-green-500' :
                                                            'bg-blue-500'
                                                        }`}></div>
                                                    <div className="flex-1">
                                                        <p className="text-sm font-medium text-gray-900 dark:text-white">
                                                            {notification.title}
                                                        </p>
                                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                                            {notification.description}
                                                        </p>
                                                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                                                            {notification.time}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>

                    {/* Profile */}
                    <div className="relative">
                        <button
                            onClick={() => setShowProfile(!showProfile)}
                            className="flex items-center space-x-3 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                            aria-label="User menu"
                        >
                            <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-full flex items-center justify-center">
                                <SafeIcon icon={FiUser} className="w-4 h-4 text-white" />
                            </div>
                            <div className="text-left">
                                <p className="text-sm font-medium text-gray-900 dark:text-white">
                                    {user ? `${user.firstName} ${user.lastName}` : 'Loading...'}
                                </p>
                                <div className="flex items-center space-x-2">
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${getRoleBadgeColor()}`}>
                                        {getRoleDisplay()}
                                    </span>
                                </div>
                            </div>
                        </button>

                        <AnimatePresence>
                            {showProfile && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 10 }}
                                    className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50"
                                >
                                    <div className="p-2">
                                        <button
                                            onClick={handleProfileClick}
                                            className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                        >
                                            <SafeIcon icon={FiUser} className="w-4 h-4" />
                                            <span>Profile</span>
                                        </button>
                                        <button
                                            onClick={handleSettingsClick}
                                            className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                                        >
                                            <SafeIcon icon={FiSettings} className="w-4 h-4" />
                                            <span>Settings</span>
                                        </button>
                                        <div className="border-t border-gray-200 dark:border-gray-700 my-2"></div>
                                        <button
                                            onClick={handleSignOut}
                                            className="w-full flex items-center space-x-2 px-3 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                        >
                                            <SafeIcon icon={FiLogOut} className="w-4 h-4" />
                                            <span>Sign Out</span>
                                        </button>
                                    </div>
                                </motion.div>
                            )}
                        </AnimatePresence>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default Header;